import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import type { BusinessGroupBy, BusinessMetricsResponse } from '../../types/businessMetrics';
import { formatBusinessDateTime } from '../../types/businessMetricsView';
import { DiscountPanel } from './DiscountPanel';
import { FunnelPanel } from './FunnelPanel';
import { BusinessAlertBand, OpsAlertsPanel } from './OpsAlertsPanel';
import { ReferralPanel } from './ReferralPanel';
import { RevenuePanel } from './RevenuePanel';
import { SubscriptionPanel } from './SubscriptionPanel';

export const BUSINESS_GROUP_BY_OPTIONS: ReadonlyArray<{ value: BusinessGroupBy; label: string }> = [
  { value: 'DAY', label: '일' },
  { value: 'WEEK', label: '주' },
  { value: 'MONTH', label: '월' },
];

export interface BusinessMetricsViewProps {
  data: BusinessMetricsResponse | null;
  loading: boolean;
  error: string | null;
  groupBy: BusinessGroupBy;
  onGroupByChange: (groupBy: BusinessGroupBy) => void;
  onReload: () => void;
}

/** 표시 전용. 데이터는 props 로만 받는다(훅과 분리 — 정적 렌더 시험용). */
export function BusinessMetricsView({ data, loading, error, groupBy, onGroupByChange, onReload }: BusinessMetricsViewProps) {
  return (
    <Card data-testid="business-metrics-section">
      <CardHeader>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <CardTitle>사업 지표</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {data
                ? `${data.startDate} ~ ${data.endDate} · ${data.timezone} 기준 · 생성 ${formatBusinessDateTime(data.generatedAt)}`
                : '매출·구독·퍼널·할인·추천·운영 경보를 pay 에서 집계합니다.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border bg-muted/40 p-1" role="group" aria-label="사업 지표 집계 단위">
              {BUSINESS_GROUP_BY_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={groupBy === option.value ? 'secondary' : 'ghost'}
                  aria-pressed={groupBy === option.value}
                  onClick={() => onGroupByChange(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={onReload} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : null}
        {loading && !data ? (
          <div role="status" className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            사업 지표를 불러오는 중입니다...
          </div>
        ) : null}
        {data ? (
          <>
            <BusinessAlertBand items={data.alerts.items} />
            <RevenuePanel revenue={data.revenue} />
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <SubscriptionPanel subscriptions={data.subscriptions} />
              <FunnelPanel funnel={data.funnel} />
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <DiscountPanel discounts={data.discounts} />
              <ReferralPanel referral={data.referral} />
            </div>
            <OpsAlertsPanel alerts={data.alerts} />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
